/**
 * Section: Sparkle Clean (QC Standard only)
 *
 * Captures whether the home needs a sparkle clean and, if the agent performed
 * one, why. A sparkle clean is NOT a separate visit type — it is recorded here
 * as part of the QC Standard visit. Only Mainstay Flex Agents are authorized to
 * complete a sparkle clean and receive compensation for it.
 */
/* eslint-disable react/jsx-no-bind */

import { useCallback } from 'preact/hooks';
import { useForm } from '@ox/visits-ui/context/FormContext';
import { Select, Textarea, Checkbox } from '@ox/visits-ui/components/shared/FormComponents';

const SPARKLE_REASONS = [
  'Dusty inside',
  'Dead bugs',
  'A spillage or mess found',
  'Leaves or debris found in home',
  'Outside cleanup needed',
  'N/A',
  'Other',
];

export function SectionSparkleClean() {
  const { formData, updateField } = useForm();

  const selectedReasons = (formData.sparkle_reasons || '')
    .split(',')
    .filter(Boolean);

  const handleNeededChange = useCallback(
    (value: string) => updateField('sparkle_needed', value),
    [updateField],
  );

  const handleAddedChange = useCallback(
    (value: string) => updateField('sparkle_added', value),
    [updateField],
  );

  const toggleReason = useCallback(
    (reason: string, checked: boolean) => {
      const current = (formData.sparkle_reasons || '').split(',').filter(Boolean);
      const next = checked
        ? Array.from(new Set([...current, reason]))
        : current.filter((r) => r !== reason);
      updateField('sparkle_reasons', next.join(','));
    },
    [formData.sparkle_reasons, updateField],
  );

  const handleReasonOtherChange = useCallback(
    (value: string) => updateField('sparkle_reason_other', value),
    [updateField],
  );

  const handleNoReasonChange = useCallback(
    (value: string) => updateField('sparkle_no_reason', value),
    [updateField],
  );

  const handleNoReasonOtherChange = useCallback(
    (value: string) => updateField('sparkle_no_reason_other', value),
    [updateField],
  );

  return (
    <div className="form-section">
      <h2>Sparkle Clean</h2>
      <p>
        A Sparkle Clean is a light property refresh to ensure homes remain clean,
        fresh, and show-ready for buyers — dusting, wiping surfaces, removing debris,
        and flushing toilets. It is not a full deep clean.
      </p>

      <Select
        label="Does this home need a sparkle clean?"
        name="sparkle_needed"
        value={formData.sparkle_needed || ''}
        onChange={handleNeededChange}
        options={[
          { label: 'Select...', value: '', disabled: true },
          { label: 'Yes', value: 'Yes' },
          { label: 'No', value: 'No' },
        ]}
        required
      />

      <Select
        label="Did you add a Sparkle Clean to this visit?"
        name="sparkle_added"
        value={formData.sparkle_added || ''}
        onChange={handleAddedChange}
        hintMessage="Only Mainstay Flex Agents may complete a Sparkle Clean and receive compensation."
        options={[
          { label: 'Select...', value: '', disabled: true },
          { label: 'Yes', value: 'Yes' },
          { label: 'No', value: 'No' },
        ]}
        required
      />

      {formData.sparkle_added === 'Yes' && (
        <div className="form-field">
          <p className="checkbox-group-label">
            If yes, what was the reason? (check all that apply)
          </p>
          {SPARKLE_REASONS.map((reason) => (
            <Checkbox
              key={reason}
              label={reason}
              name={`sparkle_reason_${reason}`}
              value={reason}
              checked={selectedReasons.includes(reason)}
              onChange={(checked) => toggleReason(reason, checked)}
            />
          ))}
          {selectedReasons.includes('Other') && (
            <Textarea
              label="Please describe the other reason"
              name="sparkle_reason_other"
              value={formData.sparkle_reason_other || ''}
              onChange={handleReasonOtherChange}
              autoSize
              required
            />
          )}
        </div>
      )}

      {formData.sparkle_added === 'No' && (
        <>
          <Select
            label="If no, why not?"
            name="sparkle_no_reason"
            value={formData.sparkle_no_reason || ''}
            onChange={handleNoReasonChange}
            options={[
              { label: 'Select...', value: '', disabled: true },
              { label: 'It did not need a clean', value: 'It did not need a clean' },
              { label: "It needed a sparkle clean but I didn't have time", value: "It needed a sparkle clean but I didn't have time" },
              { label: 'It needed a full clean not just a sparkle', value: 'It needed a full clean not just a sparkle' },
              { label: 'I am not a Flex Agent', value: 'I did not complete a sparkle clean because I am not a Flex Agent.' },
              { label: 'I am not a Flex Agent, but I am interested in becoming one', value: 'I did not complete a sparkle clean because I am not a Flex Agent, but I am interested in becoming one.' },
              { label: 'N/A', value: 'N/A' },
              { label: 'Other', value: 'Other' },
            ]}
            required
          />
          {formData.sparkle_no_reason === 'Other' && (
            <Textarea
              label="Please describe the other reason"
              name="sparkle_no_reason_other"
              value={formData.sparkle_no_reason_other || ''}
              onChange={handleNoReasonOtherChange}
              autoSize
              required
            />
          )}
        </>
      )}
    </div>
  );
}
